import React, { useState, useMemo } from 'react';
import { Table, Button } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { ReportReservationResponseV4, TotalSourceV4 } from '../../../../services/cm-report/response/ReportReservationResponseV4';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import { IconArrowUp, IconArrowDown } from '@tabler/icons-react';

interface ReportReservationTableV4Props {
    data: ReportReservationResponseV4;
}

const ReportReservationTableV4: React.FC<ReportReservationTableV4Props> = ({ data }) => {
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

    const monthHeaders = data.total_month.map(item => item.month);
    const formattedMonthHeaders = monthHeaders.map(month => format(new Date(`${month}-01`), "MMMM yyyy"));

    const processedData = data.data.reduce<Record<string, Record<string, number>>>((acc, item) => {
        if (!acc[item.name]) acc[item.name] = {};
        acc[item.name][item.month] = item.total_earning;
        return acc;
    }, {});

    const sortedSources = useMemo(() => {
        const sortableItems = [...data.total_source];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                let aValue, bValue;
                if (monthHeaders.includes(sortConfig.key)) {
                    aValue = processedData[a.name]?.[sortConfig.key] ?? 0;
                    bValue = processedData[b.name]?.[sortConfig.key] ?? 0;
                } else if (sortConfig.key === 'source_name') {
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                } else { // 'total'
                    aValue = a.total_earning;
                    bValue = b.total_earning;
                }

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [data.total_source, processedData, sortConfig, monthHeaders]);

    const handleSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'desc';
        if (key === 'source_name') direction = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === direction) {
            direction = direction === 'asc' ? 'desc' : 'asc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key: string) => {
        if (!sortConfig || sortConfig.key !== key) return null;
        return sortConfig.direction === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />;
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Reservation Report V4", 14, 10);

        const tableData = sortedSources.map(source => [
            source.name,
            ...monthHeaders.map(month => {
                const value = processedData[source.name]?.[month] ?? 0;
                return value === 0 ? '-' : formatPrice(value);
            }),
            source.total_earning === 0 ? '-' : formatPrice(source.total_earning)
        ]);

        const totalRow = [
            "Total",
            ...data.total_month.map(month => month.total_earning === 0 ? '-' : formatPrice(month.total_earning)),
            data.total === 0 ? '-' : formatPrice(data.total)
        ];

        autoTable(doc, {
            head: [["Source", ...formattedMonthHeaders, "Total"]],
            body: [...tableData, totalRow]
        });

        doc.save("ReservationReportV4.pdf");
    };

    const exportToExcel = () => {
        const header = ["Source", ...formattedMonthHeaders, "Total"];
        const rows = sortedSources.map(source => [
            source.name,
            ...monthHeaders.map(month => {
                const value = processedData[source.name]?.[month] ?? 0;
                return value === 0 ? '-' : formatPrice(value);
            }),
            source.total_earning === 0 ? '-' : formatPrice(source.total_earning)
        ]);
        const footer = [
            "Total",
            ...data.total_month.map(month => month.total_earning === 0 ? '-' : formatPrice(month.total_earning)),
            data.total === 0 ? '-' : formatPrice(data.total)
        ];

        const wsData = [header, ...rows, footer];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Reservation Report V4");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, "ReservationReportV4.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('source_name')} style={{ cursor: 'pointer' }}>
                                <div className="d-flex align-items-center justify-content-between">Source{getSortIcon('source_name')}</div>
                            </th>
                            {formattedMonthHeaders.map((month, index) => (
                                <th key={index} onClick={() => handleSort(monthHeaders[index])} style={{ cursor: 'pointer' }}>
                                    <div className="d-flex align-items-center justify-content-between">{month}{getSortIcon(monthHeaders[index])}</div>
                                </th>
                            ))}
                            <th onClick={() => handleSort('total')} style={{ cursor: 'pointer' }}>
                                <div className="d-flex align-items-center justify-content-between">Total{getSortIcon('total')}</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedSources.map((source: TotalSourceV4) => (
                            <tr key={source.name}>
                                <td>{source.name}</td>
                                {monthHeaders.map((month, index) => {
                                    const cellValue = processedData[source.name]?.[month] ?? 0;
                                    return <td key={index}>{cellValue === 0 ? '-' : formatPrice(cellValue)}</td>;
                                })}
                                <td><strong>{source.total_earning === 0 ? '-' : formatPrice(source.total_earning)}</strong></td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Total</strong></td>
                            {data.total_month.map((monthTotal, index) => (
                                <td key={index}><strong>{monthTotal.total_earning === 0 ? '-' : formatPrice(monthTotal.total_earning)}</strong></td>
                            ))}
                            <td><strong>{data.total === 0 ? '-' : formatPrice(data.total)}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default ReportReservationTableV4;